import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";

/**
 * Brief 3.5 PR 3 — "Re-review recommended" ribbon.
 *
 * Renders at the top of the Review tab on approved/live merchants when
 * the current verdict diverges from the lifecycle status (i.e. the
 * merchant would be flagged as a blocker under the current rules
 * version, but is currently approved/live).
 *
 * Consumes the shared `verdict` prop from `useMerchantVerdict` —
 * single source of truth across the page (no extra round trip).
 *
 * Suppressed if:
 *   • Merchant is not approved/live (nothing retroactive to flag).
 *   • Verdict icon is not "blocker".
 *   • Super_admin has dismissed at the current rules version
 *     (server-side filter; ribbon never sees blocker icon for those).
 *   • User is not super_admin (re-review is super_admin only per brief).
 */
export default function ReReviewRibbon({ merchantId, identity, verdict }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const isApprovedOrLive = identity?.status === "approved" || identity?.status === "live";
  const canSee = user?.role === "super_admin";

  if (!isApprovedOrLive || !canSee) return null;
  if (!verdict || verdict.icon !== "blocker") return null;
  if (dismissed) return null;

  return (
    <div
      data-testid="merchant-re-review-ribbon"
      className="rounded-2xl border border-amber-400/30 bg-amber-500/[0.05] px-4 py-3 flex items-start gap-3"
    >
      <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-200" />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] text-amber-50">
          This merchant would be flagged as a blocker under updated rules.
          {" "}
          {verdict.primary_blocker && (
            <span className="text-amber-100/90" data-testid="merchant-re-review-ribbon-reason">
              Reason: {verdict.primary_blocker}.
            </span>
          )}
        </div>
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            data-testid="merchant-re-review-ribbon-open"
            onClick={() => navigate("/admin/compliance/re-review")}
            className="inline-flex items-center text-[12.5px] text-amber-100 underline-offset-2 hover:underline"
          >
            Review changes →
          </button>
          <button
            type="button"
            data-testid="merchant-re-review-ribbon-dismiss"
            onClick={async () => {
              try {
                await api.post(
                  `/admin/compliance/re-review/${merchantId}/dismiss`,
                );
                setDismissed(true);
              } catch (e) {
                alert(`Could not dismiss: ${e?.response?.data?.detail || "unknown"}`);
              }
            }}
            className="text-[12px] text-amber-200/70 hover:text-amber-100"
          >
            Dismiss until rules change
          </button>
        </div>
      </div>
    </div>
  );
}
